import { createContext, useContext } from "react";

/**
 * CaptureSegmentDropContext — the one channel between the Captured drawer (the
 * drag SOURCE and the "Add to Segment" picker) and the builder canvas (the drop
 * TARGET).
 *
 * They sit in different branches of the builder's tree: the drawer is a fixed
 * overlay mounted at the WheelLayout root, the canvas is deep inside the layout
 * grid. Threading `place()` down both branches as props would mean editing every
 * component in between, so the provider that owns the insert publishes it here
 * and each side reads it directly.
 *
 * The default is deliberately INERT rather than absent: with the flag OFF, or
 * outside the builder, `isEnabled` is false and `place()` resolves to a stated
 * refusal. A consumer therefore never has to null-check, and can never silently
 * do nothing — silence is the exact failure this whole feature exists to remove.
 */

export const CAPTURE_DROP_DISABLED_MESSAGE =
  "Open a Segment in the builder first, then add the capture to it.";

export const INERT_CAPTURE_DROP = Object.freeze({
  isEnabled: false,
  wheelSections: Object.freeze([]),
  activeSegmentId: null,
  isPlacing: false,
  place: () =>
    Promise.resolve({ ok: false, message: CAPTURE_DROP_DISABLED_MESSAGE }),
  createSegmentAndPlace: () =>
    Promise.resolve({ ok: false, message: CAPTURE_DROP_DISABLED_MESSAGE }),
});

const CaptureSegmentDropContext = createContext(INERT_CAPTURE_DROP);

/** Read the capture→Segment channel. Always returns a usable object. */
export const useCaptureSegmentDrop = () =>
  useContext(CaptureSegmentDropContext) || INERT_CAPTURE_DROP;

export default CaptureSegmentDropContext;
